export interface HashWindowOptions {
  /**
   * Storybook and similar tools render components inside a same-origin iframe.
   * Set this to true when the visible parent URL should stay in sync too.
   */
  includeSameOriginParent?: boolean;
  targetWindow?: Window;
}

function getDefaultWindow(): Window | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

function getSameOriginParentWindow(targetWindow: Window | undefined): Window | undefined {
  if (!targetWindow || targetWindow.parent === targetWindow) return undefined;

  try {
    return targetWindow.parent.location.origin === targetWindow.location.origin ? targetWindow.parent : undefined;
  } catch {
    return undefined;
  }
}

function stripLeadingHash(value: string): string {
  return value.startsWith('#') ? value.slice(1) : value;
}

/**
 * Returns the current URL hash fragment (without the leading `#`), decoded.
 * Uses the same-origin parent window first when `includeSameOriginParent` is set
 * because Storybook-embedded canvases run in an iframe while the user edits the
 * visible parent URL.
 */
export function getHashSlug(options: HashWindowOptions = {}): string {
  const targetWindow = options.targetWindow ?? getDefaultWindow();
  if (!targetWindow) return '';

  const parentWindow = options.includeSameOriginParent ? getSameOriginParentWindow(targetWindow) : undefined;
  if (parentWindow) {
    return decodeHashValue(stripLeadingHash(parentWindow.location.hash));
  }

  const currentHash = stripLeadingHash(targetWindow.location.hash);
  return decodeHashValue(currentHash);
}

function decodeHashValue(raw: string): string {
  if (!raw) return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Writes the hash fragment using `history.replaceState` so the browser neither
 * adds a history entry nor triggers the native scroll-to-id jump. Pass an empty
 * string to clear the hash.
 */
export function setHashSlug(slug: string, options: HashWindowOptions = {}): void {
  const targetWindow = options.targetWindow ?? getDefaultWindow();
  if (!targetWindow) return;

  const updateWindowHash = (windowToUpdate: Window) => {
    const url = new URL(windowToUpdate.location.href);
    url.hash = slug ? encodeURIComponent(slug) : '';
    windowToUpdate.history.replaceState(windowToUpdate.history.state, '', url.toString());
  };

  updateWindowHash(targetWindow);

  if (options.includeSameOriginParent) {
    const parentWindow = getSameOriginParentWindow(targetWindow);
    if (parentWindow) {
      updateWindowHash(parentWindow);
    }
  }
}

/**
 * Subscribes to `hashchange` and `popstate` on the target window (and optionally
 * the same-origin parent). Returns an unsubscribe function.
 */
export function addHashChangeListener(listener: () => void, options: HashWindowOptions = {}): () => void {
  const targetWindow = options.targetWindow ?? getDefaultWindow();
  if (!targetWindow) return () => undefined;

  const parentWindow = options.includeSameOriginParent ? getSameOriginParentWindow(targetWindow) : undefined;
  targetWindow.addEventListener('hashchange', listener);
  targetWindow.addEventListener('popstate', listener);
  parentWindow?.addEventListener('hashchange', listener);
  parentWindow?.addEventListener('popstate', listener);

  return () => {
    targetWindow.removeEventListener('hashchange', listener);
    targetWindow.removeEventListener('popstate', listener);
    parentWindow?.removeEventListener('hashchange', listener);
    parentWindow?.removeEventListener('popstate', listener);
  };
}
